import React from 'react';
import { connect } from 'react-redux';
import { MAX_CALL_PARTICIPANTS } from '../../../../../shared/call-capacity';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import Icon from '../../../base/icons/components/Icon';
import { IconInterpreting } from '../../../base/icons/svg';
import { isClient } from '../../../base/user-role/functions';
import { queueService } from '../../../interpreter-queue/InterpreterQueueService';
import { cancelInterpreterRequest, requestInterpreter } from '../../../interpreter-queue/actions';
import { getDefaultRequestLanguage } from '../../../interpreter-queue/interpreterLanguages';
import { canRequestQueueInterpreterInRoom, isP2PCallOriginator } from '../../../interpreter-queue/p2pInterpreterEligibility';
import { showNotification } from '../../../notifications/actions';
import { isCompactCallBar } from '../../clientCallActions.web';
const BASE_STYLE = {
    alignItems: 'center',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    borderRadius: 8,
    boxShadow: '0 8px 22px rgba(0, 0, 0, 0.28)',
    color: '#fff',
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: 13,
    fontWeight: 800,
    gap: 8,
    height: 40,
    justifyContent: 'center',
    letterSpacing: 0,
    margin: '0 8px',
    minWidth: 156,
    padding: '0 16px',
    textTransform: 'none',
    transition: 'background 160ms ease, border-color 160ms ease, transform 160ms ease'
};
const STATE_STYLES = {
    idle: {
        background: '#2357C6',
        borderColor: '#5E8CFF'
    },
    pending: {
        background: '#D99A12',
        borderColor: '#FFD166',
        color: '#111827'
    },
    matched: {
        background: '#15803D',
        borderColor: '#4ADE80'
    }
};
// On a narrow bar: a key the size of the bar's others, in the state colour.
const COMPACT_STYLE = {
    height: 48,
    margin: 0,
    minWidth: 0,
    padding: 0,
    width: 48
};
const DOT_STYLES = {
    idle: { background: '#C7D2FE' },
    pending: { background: '#111827' },
    matched: { background: '#BBF7D0' }
};
const RequestInterpreterButton = ({ _callIsFull, _compact, _interpreterName, _isConnected, _isRequestPending, _matchFound, dispatch, visible = true }) => {
    if (!visible) {
        return null;
    }
    const state = _matchFound ? 'matched' : _isRequestPending ? 'pending' : 'idle';
    const label = _matchFound
        ? 'Interpreter Confirmed'
        : _isRequestPending
            ? 'Interpreter Requested'
            : 'Request Interpreter';
    const title = _matchFound
        ? (_interpreterName
            ? `${_interpreterName} accepted and is joining`
            : 'An interpreter accepted and is joining')
        : _isRequestPending
            ? 'Cancel interpreter request'
            : 'Request a sign language interpreter';
    const handleClick = () => {
        sendAnalytics(createToolbarEvent('requestInterpreter'));
        if (_matchFound) {
            return;
        }
        if (_isRequestPending) {
            dispatch(cancelInterpreterRequest());
            return;
        }
        // An interpreter takes one of the eight places (the room pill's cap).
        if (_callIsFull) {
            dispatch(showNotification({
                titleKey: 'vrs.callFullForInterpreter'
            }, 'medium'));
            return;
        }
        if (!_isConnected || !queueService.isConnected()) {
            dispatch(showNotification({
                titleKey: 'vrs.queueUnavailable'
            }, 'medium'));
            return;
        }
        // The button's own label shows the pending request; no toast after the tap.
        dispatch(requestInterpreter(getDefaultRequestLanguage()));
    };
    const wideStyle = {
        ...BASE_STYLE,
        ...STATE_STYLES[state]
    };
    const compactStyle = {
        ...wideStyle,
        ...COMPACT_STYLE
    };
    return (React.createElement("button", { "aria-label": label, "aria-pressed": _isRequestPending || _matchFound, "data-vrs-interpreter-button": 'react', onClick: handleClick, style: _compact ? compactStyle : wideStyle, title: title, type: 'button' }, _compact ? (React.createElement(Icon, { color: STATE_STYLES[state].color ?? '#fff', size: 22, src: IconInterpreting })) : (React.createElement(React.Fragment, null,
        React.createElement("span", { "aria-hidden": 'true', style: {
                ...DOT_STYLES[state],
                borderRadius: 999,
                display: 'inline-block',
                height: 9,
                width: 9
            } }),
        label))));
};
function _mapStateToProps(state) {
    const queueState = state['features/interpreter-queue'];
    const matchFound = Boolean(queueState?.matchFound);
    return {
        _callIsFull: state['features/base/participants'].remote.size + 1 >= MAX_CALL_PARTICIPANTS,
        _compact: isCompactCallBar(state),
        _interpreterName: queueState?.matchData?.interpreterName,
        _isConnected: Boolean(queueState?.isConnected),
        _isRequestPending: Boolean(queueState?.isRequestPending),
        _matchFound: matchFound,
        /*
         * Role was the only condition, so every client saw "Request
         * Interpreter" in the in-call toolbar — including a client already in
         * the room with the interpreter who had accepted them. It sat there
         * reading "Interpreter Confirmed" for the whole call, offering to
         * arrange something that had already happened.
         *
         * The idle and pending states are still worth having: the pending state
         * is the only feedback that the request went out. Once an interpreter
         * has accepted, this room is that call — the button has nothing left to
         * do, and matchFound is cleared on INTERPRETED_CALL_ENDED so the next
         * call gets it back.
         *
         * Never in a platform call's room (p2p-/group-): there anyone in the
         * call brings an interpreter in, with the green interpreting button
         * (canRequestP2PInterpreter). The person who answered used to be
         * offered this queue request instead, which pulled them out of the call.
         */
        visible: isClient() && !matchFound && !isP2PCallOriginator()
            && canRequestQueueInterpreterInRoom(state['features/base/conference']?.room)
    };
}
export default connect(_mapStateToProps)(RequestInterpreterButton);
